import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../components/api';
import Loader from '../Loader/Loader';
import './Compatibility.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import PageHeader from '../Page/PageHeader';
import { Helmet } from 'react-helmet';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.astralight.xyz&hl=fr';

const Compatibility = () => {
    const { guid } = useParams();
    const navigate = useNavigate();
    const [loadingState, setLoadingState] = useState('idle');
    const [error, setError] = useState(null);
    const [compatibilityData, setCompatibilityData] = useState(null);
    const [citySuggestions1, setCitySuggestions1] = useState([]);
    const [citySuggestions2, setCitySuggestions2] = useState([]);
    const [showSuggestions1, setShowSuggestions1] = useState(false);
    const [showSuggestions2, setShowSuggestions2] = useState(false);
    const [citySearch1, setCitySearch1] = useState('');
    const [citySearch2, setCitySearch2] = useState('');
    const [formData, setFormData] = useState({
        person1: {
            first_name: '',
            birth_date: '',
            birth_time: '',
            city: '',
            latitude: '',
            longitude: ''
        },
        person2: {
            first_name: '',
            birth_date: '',
            birth_time: '',
            city: '',
            latitude: '',
            longitude: ''
        }
    });

    // Textes pour le SEO
    const seoContent = {
        faq: [
            {
                question: "La compatibilité est-elle déterminée par les signes ou les ascendants ?",
                answer: "Les deux sont importants, mais une synastrie complète analyse bien plus : positions planétaires, aspects entre les deux thèmes, placements en maisons. Le signe solaire n'est qu'un facteur parmi de nombreux indicateurs."
            },
            {
                question: "Peut-on être astrologiquement incompatibles et vivre une relation heureuse ?",
                answer: "Absolument. L'astrologie révèle des tendances et potentiels, pas des destinées figées. La conscience des défis peut même renforcer une relation. Le libre arbitre et l'évolution personnelle transcendent toujours les prédispositions astrologiques."
            },
            {
                question: "Quelle est la différence entre synastrie et thème composite ?",
                answer: "La synastrie compare deux thèmes distincts pour analyser leurs interactions. Le thème composite crée un nouveau thème 'fusionné' représentant la relation comme entité unique. Les deux approches sont complémentaires pour une analyse relationnelle complète."
            }
        ]
    };

    // Composant pour l'article informatif sur la synastrie
    const SynastrieInfoContent = () => (
        <div className="article panel">
            <h2>La synastrie, c'est quoi ?</h2>
            <p>
                La synastrie est une branche spécialisée de l'astrologie qui étudie les interactions entre les thèmes astraux de deux personnes. Plus qu'un simple test de compatibilité, elle révèle la dynamique énergétique complexe qui se crée lorsque deux cartes du ciel entrent en relation.
            </p>
            <p>
                Cette analyse approfondie examine comment les planètes d'une personne forment des aspects (angles) avec les planètes de l'autre, créant ainsi des résonances harmonieuses ou des tensions dynamiques. Chaque relation possède sa propre "empreinte astrologique" unique, aussi complexe et nuancée que les individus qui la composent.
            </p>
            <p>
                Contrairement aux simplifications populaires qui ne considèrent que les signes solaires, la synastrie professionnelle prend en compte l'ensemble des facteurs astrologiques : positions planétaires, maisons, aspects, points sensibles et plus encore. C'est cette vision holistique qui permet une compréhension véritable des potentiels relationnels.
            </p>

            <h2>Comment calculons-nous votre compatibilité ?</h2>
            <p>
                Notre calcul de compatibilité astrologique repose sur une méthodologie en plusieurs étapes, combinant l'astrologie traditionnelle et les approches modernes pour une analyse précise et nuancée.
            </p>
            <p>
                Premièrement, nous établissons les <Link to="/calcul-theme-astral">thèmes natals complets</Link> des deux personnes, en utilisant leurs données de naissance exactes (date, heure, lieu). Ces positions planétaires fondent toute l'analyse. Ensuite, notre algorithme identifie tous les aspects interplanétaires entre les deux thèmes - conjonctions, trigones, carrés, oppositions et autres angles significatifs.
            </p>
            <p>
                Nous accordons une attention particulière aux interactions entre planètes personnelles : <Link to="/blog/planetes/soleil">Soleil</Link>-<Link to="/blog/planetes/lune">Lune</Link> (identité et émotions), <Link to="/blog/planetes/venus">Vénus</Link>-<Link to="/blog/planetes/mars">Mars</Link> (attraction et passion), <Link to="/blog/planetes/mercure">Mercure</Link> (communication), ainsi qu'aux aspects impliquant l'Ascendant et les angles des maisons. Notre système pondère également l'importance des aspects selon leur nature, précision et planètes impliquées.
            </p>
            <p>
                Le résultat final est un score global et des analyses sectorielles qui révèlent non seulement le potentiel d'harmonie, mais aussi les zones de croissance et d'évolution au sein de la relation.
            </p>
        </div>
    );

    // Composant pour la compréhension des scores
    const ScoreGuide = () => {
        // Fonction utilitaire pour créer des liens vers les planètes
        const getPlanetLink = (planetName, plural = false) => {
            const planetMap = {
                'Soleil': '/blog/planetes/soleil',
                'Lune': '/blog/planetes/lune',
                'Mercure': '/blog/planetes/mercure',
                'Vénus': '/blog/planetes/venus',
                'Mars': '/blog/planetes/mars',
                'Jupiter': '/blog/planetes/jupiter',
                'Saturne': '/blog/planetes/saturne',
                'Uranus': '/blog/planetes/uranus',
                'Neptune': '/blog/planetes/neptune',
                'Pluton': '/blog/planetes/pluton',
            };

            const baseName = planetName.replace(/s$/, '');
            const displayName = plural ? `${planetName}s` : planetName;

            return planetMap[baseName] ?
                <Link to={planetMap[baseName]}>{displayName}</Link> :
                displayName;
        };

        return (
            <div className="interpretation panel">
                <h2>Comprendre votre score de compatibilité</h2>

                <h3>Attraction Mutuelle</h3>
                <p>
                    Ce score évalue l'alchimie naturelle et instinctive entre deux personnes. Il analyse principalement les liens entre {getPlanetLink('Vénus')}, {getPlanetLink('Mars')} et {getPlanetLink('Lune', true)} en synastrie. Une forte compatibilité ici indique une connexion immédiate, une attirance physique et émotionnelle puissante.
                </p>

                <h3>Communication</h3>
                <p>
                    Cette section examine comment vous échangez et vous comprenez. Les positions de {getPlanetLink('Mercure')} et les éléments d'Air révèlent votre capacité à dialoguer, à vous comprendre intellectuellement et à résoudre les malentendus. Un bon score ici signifie une entente fluide au quotidien.
                </p>

                <h3>Valeurs Communes</h3>
                <p>
                    Les positions de {getPlanetLink('Jupiter')}, {getPlanetLink('Saturne')} et {getPlanetLink('Vénus')} en synastrie révèlent si vous partagez une vision commune du monde, des priorités de vie et des aspirations profondes. Ce score reflète votre alignement fondamental en tant que couple.
                </p>

                <h3>Gestion des Conflits</h3>
                <p>
                    Aucune relation n'est sans tensions. Les aspects entre {getPlanetLink('Mars')} et {getPlanetLink('Saturne')}, ainsi que les aspects durs entre planètes personnelles, révèlent comment vous gérez les désaccords et les frictions. Un score élevé indique une capacité à résoudre les conflits de manière constructive.
                </p>

                <h3>Potentiel Long Terme</h3>
                <p>
                    Ce score évalue la solidité et la durabilité de la relation. Les liens entre {getPlanetLink('Saturne')} et {getPlanetLink('Lune', true)}, ainsi que les aspects de stabilité, indiquent si votre relation peut s'inscrire dans la durée et traverser les épreuves du temps.
                </p>
            </div>
        )
    };

    // Composant pour la FAQ
    const FAQ = () => {
        // Fonction utilitaire pour créer des liens vers les signes
        const getSignLink = (signName) => {
            const signMap = {
                'Bélier': '/blog/signes/belier',
                'Taureau': '/blog/signes/taureau',
                'Gémeaux': '/blog/signes/gemeaux',
                'Cancer': '/blog/signes/cancer',
                'Lion': '/blog/signes/lion',
                'Vierge': '/blog/signes/vierge',
                'Balance': '/blog/signes/balance',
                'Scorpion': '/blog/signes/scorpion',
                'Sagittaire': '/blog/signes/sagittaire',
                'Capricorne': '/blog/signes/capricorne',
                'Verseau': '/blog/signes/verseau',
                'Poissons': '/blog/signes/poissons'
            };
            
            return signMap[signName] ? <Link to={signMap[signName]}>{signName}</Link> : signName;
        };
        
        // Version enrichie des questions FAQ avec des liens
        const enrichedFaq = [
            {
                question: "La compatibilité est-elle déterminée par les signes ou les ascendants ?",
                answer: <>Les deux sont importants, mais une synastrie complète analyse bien plus : positions planétaires, aspects entre les deux thèmes, placements en maisons. Le <Link to="/blog/planetes/soleil">signe solaire</Link> n'est qu'un facteur parmi de nombreux indicateurs.</>
            },
            {
                question: "Peut-on être astrologiquement incompatibles et vivre une relation heureuse ?",
                answer: <>Absolument. L'astrologie révèle des tendances et potentiels, pas des destinées figées. La conscience des défis peut même renforcer une relation. Le libre arbitre et l'évolution personnelle transcendent toujours les prédispositions astrologiques.</>
            },
            {
                question: "Quelle est la différence entre synastrie et thème composite ?",
                answer: <>La synastrie compare deux <Link to="/calcul-theme-astral">thèmes</Link> distincts pour analyser leurs interactions. Le thème composite crée un nouveau thème 'fusionné' représentant la relation comme entité unique. Les deux approches sont complémentaires pour une analyse relationnelle complète.</>
            },
            {
                question: "Quels sont les couples de signes traditionnellement compatibles ?",
                answer: <>Plusieurs combinaisons sont reconnues pour leur harmonie naturelle : {getSignLink('Bélier')} et {getSignLink('Lion')} partagent une énergie de feu dynamique, {getSignLink('Taureau')} et {getSignLink('Cancer')} une sensibilité terrestre et émotionnelle, {getSignLink('Gémeaux')} et {getSignLink('Balance')} une connexion intellectuelle aérienne, et {getSignLink('Scorpion')} et {getSignLink('Poissons')} une profondeur intuitive. Mais n'oubliez pas que la synastrie complète va bien au-delà des seuls signes solaires.</>
            }
        ];
        
        return (
            <div className="faq panel">
                <h2>Questions fréquentes sur la compatibilité astrologique</h2>
                
                <div className="faq-items" itemScope itemType="https://schema.org/FAQPage">
                    {enrichedFaq.map((item, index) => (
                        <div key={index} className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                            <details>
                                <summary itemProp="name">{item.question}</summary>
                                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                    <p itemProp="text">{item.answer}</p>
                                </div>
                            </details>
                        </div>
                    ))}
                </div>
                
                <div className="additional-resources" style={{ marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #eaeaea" }}>
                    <h3>Ressources supplémentaires</h3>
                    <p>Pour une analyse approfondie de votre profil astrologique individuel, consultez notre <Link to="/calcul-theme-astral">calculateur de thème astral complet</Link>.</p>
                </div>
            </div>
        );
    };

    // Composant pour les actions de partage (adapté de Astra.js)
    const ShareActions = ({ compatGuid }) => {
        const [copied, setCopied] = useState(false);
        const [bookmarkMsg, setBookmarkMsg] = useState('');

        if (!compatGuid) return null;

        const shareUrl = `${window.location.origin}/compatibilite-amoureuse-astrologie/${compatGuid}`;
        const name1 = compatibilityData?.person1?.first_name;
        const name2 = compatibilityData?.person2?.first_name;
        const pairText = name1 && name2 ? `${name1} & ${name2}` : 'notre compatibilité astrologique';
        const shareText = `Découvrez ${pairText} sur AstraLight`;
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedText = encodeURIComponent(`${shareText} ${shareUrl}`);

        const handleCopyLink = async () => {
            try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                try {
                    const textarea = document.createElement('textarea');
                    textarea.value = shareUrl;
                    textarea.style.position = 'fixed';
                    textarea.style.left = '-9999px';
                    document.body.appendChild(textarea);
                    textarea.focus();
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                } catch (e) {
                    console.error('Impossible de copier le lien', e);
                }
            }
        };

        const handleWebShare = async () => {
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: 'Compatibilité astrologique',
                        text: shareText,
                        url: shareUrl
                    });
                } catch (e) {
                    // Ignored (user canceled)
                }
            } else {
                handleCopyLink();
            }
        };

        const addToFavorites = () => {
            const title = document.title || 'AstraLight - Compatibilité';
            try {
                if (window.external && 'AddFavorite' in window.external) {
                    window.external.AddFavorite(shareUrl, title);
                    return;
                }
                if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(title, shareUrl, '');
                    return;
                }
            } catch (e) {}

            const isMac = navigator.platform && navigator.platform.toUpperCase().includes('MAC');
            const shortcut = isMac ? 'Cmd + D' : 'Ctrl + D';
            setBookmarkMsg(`Astuce: appuyez sur ${shortcut} pour ajouter cette page aux favoris`);
            setTimeout(() => setBookmarkMsg(''), 4000);
        };

        return (
            <div className="panel share-actions sky" style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'center' }}>
                <h3 style={{ margin: 0 }}>Partager cette compatibilité</h3>
                <div className="share-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                    <button onClick={handleWebShare} className="icon-button" aria-label="Partager">
                        <i className="fa-solid fa-share-nodes" aria-hidden="true"></i>
                    </button>
                    <button onClick={handleCopyLink} className="icon-button" aria-label="Copier le lien">
                        <i className="fa-regular fa-copy" aria-hidden="true"></i>
                    </button>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur Facebook">
                        <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
                    </a>
                    <a href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent('Compatibilité astrologique ✨')}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur X / Twitter">
                        <i className="fa-brands fa-x-twitter" aria-hidden="true"></i>
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur LinkedIn">
                        <i className="fa-brands fa-linkedin-in" aria-hidden="true"></i>
                    </a>
                    <a href={`https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent('Compatibilité astrologique ✨')}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur Telegram">
                        <i className="fa-brands fa-telegram" aria-hidden="true"></i>
                    </a>
                    <a href={`https://wa.me/?text=${encodedText}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur WhatsApp">
                        <i className="fa-brands fa-whatsapp" aria-hidden="true"></i>
                    </a>
                    <a href={`mailto:?subject=${encodeURIComponent('Compatibilité astrologique')}&body=${encodedText}`} className="icon-button" aria-label="Partager par email">
                        <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                    </a>
                    <button onClick={addToFavorites} className="icon-button" aria-label="Ajouter aux favoris">
                        <i className="fa-regular fa-bookmark" aria-hidden="true"></i>
                    </button>
                </div>
                {copied && <small style={{ opacity: 0.8 }}>Lien copié ✓</small>}
                {bookmarkMsg && <small style={{ opacity: 0.8 }}>{bookmarkMsg}</small>}
            </div>
        );
    };

    // Schema.org pour l'outil de calcul
    const getToolSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Calculateur de Synastrie Gratuit",
            "applicationCategory": "Astrologie",
            "operatingSystem": "Web",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR"
            },
            "description": "Calculez votre synastrie de couple gratuitement : aspects entre deux thèmes astraux, scores de compatibilité et interprétation complète."
        };
    };

    // Schema.org pour les compatibilités astrologiques
    // const getPopularPairsSchema = () => {
    //     return {
    //         "@context": "https://schema.org",
    //         "@type": "ItemList",
    //         "name": "Compatibilités astrologiques populaires",
    //         "description": "Les combinaisons de signes astrologiques les plus consultées pour la compatibilité amoureuse",
    //         "itemListElement": [
    //             {
    //                 "@type": "ListItem",
    //                 "position": 1,
    //                 "name": "Compatibilité Lion et Bélier",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 2,
    //                 "name": "Compatibilité Taureau et Cancer",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 3,
    //                 "name": "Compatibilité Gémeaux et Balance",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 4,
    //                 "name": "Compatibilité Scorpion et Poissons",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 5,
    //                 "name": "Compatibilité Verseau et Sagittaire",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             }
    //         ]
    //     };
    // };

    // Ajoutez ces refs
    const suggestionTimeoutRef1 = useRef(null);
    const suggestionTimeoutRef2 = useRef(null);

    // Fonction pour rechercher des villes avec Nominatim (comme dans Astra.js)
    const searchCities = async (query, personNumber) => {
        console.log(`🔍 Recherche de villes pour personne ${personNumber}:`, query);
        if (query.length < 3) {
            console.log('❌ Requête trop courte, minimum 3 caractères');
            if (personNumber === 1) {
                setCitySuggestions1([]);
                setShowSuggestions1(false);
            } else {
                setCitySuggestions2([]);
                setShowSuggestions2(false);
            }
            return;
        }

        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&featuretype=city`;
            console.log('📡 Appel API Nominatim:', url);

            const response = await fetch(url);
            const data = await response.json();
            console.log('📥 Données reçues de Nominatim:', data);

            const formattedSuggestions = data.map(item => ({
                display: `${item.name}, ${item.display_name.split(', ').slice(1).join(', ')}`,
                lat: item.lat,
                lon: item.lon,
                name: item.name
            }));

            console.log('✨ Suggestions formatées:', formattedSuggestions);
            if (personNumber === 1) {
                setCitySuggestions1(formattedSuggestions);
                setShowSuggestions1(true);
            } else {
                setCitySuggestions2(formattedSuggestions);
                setShowSuggestions2(true);
            }
        } catch (error) {
            console.error('❌ Erreur lors de la recherche des villes:', error);
        }
    };

    // Gestion du changement de champ pour une personne spécifique
    const handlePersonChange = (e, personNumber) => {
        const { name, value } = e.target;
        const personKey = `person${personNumber}`;

        setFormData({
            ...formData,
            [personKey]: {
                ...formData[personKey],
                [name]: value
            }
        });
    };

    // Gestion de la recherche de ville
    const handleCitySearch = (e, personNumber) => {
        const value = e.target.value;
        
        if (personNumber === 1) {
            setCitySearch1(value);
            
            // Debounce la recherche pour personne 1
            if (suggestionTimeoutRef1.current) {
                clearTimeout(suggestionTimeoutRef1.current);
            }
            
            suggestionTimeoutRef1.current = setTimeout(() => {
                searchCities(value, 1);
            }, 300);
        } else {
            setCitySearch2(value);
            
            // Debounce la recherche pour personne 2
            if (suggestionTimeoutRef2.current) {
                clearTimeout(suggestionTimeoutRef2.current);
            }
            
            suggestionTimeoutRef2.current = setTimeout(() => {
                searchCities(value, 2);
            }, 300);
        }
    };

    // Sélection d'une ville dans les suggestions
    const handleCitySelect = (suggestion, personNumber) => {
        const personKey = `person${personNumber}`;
        
        setFormData({
            ...formData,
            [personKey]: {
                ...formData[personKey],
                city: suggestion.name,
                latitude: suggestion.lat,
                longitude: suggestion.lon
            }
        });

        if (personNumber === 1) {
            setCitySearch1(suggestion.display);
            setShowSuggestions1(false);
        } else {
            setCitySearch2(suggestion.display);
            setShowSuggestions2(false);
        }
    };

    // Chargement par GUID
    useEffect(() => {
        const loadByGuid = async (g) => {
            try {
                setLoadingState('loading');
                const response = await api.get(`/compatibility/${g}`);
                if (response.data.success) {
                    setCompatibilityData(response.data.data);
                    setLoadingState('success');
                } else {
                    setError(response.data.error || 'Compatibilité introuvable.');
                    setLoadingState('error');
                }
            } catch (err) {
                setError(err.response?.data?.error || 'Erreur lors du chargement de la compatibilité.');
                setLoadingState('error');
            }
        };
        if (guid) {
            loadByGuid(guid);
        }
    }, [guid]);

    // Soumission du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingState('loading');
        setError(null);

        try {
            console.log('📤 Envoi des données au backend:', formData);
            const response = await api.post('/calculate_compatibility', formData);
            console.log('📥 Réponse du backend:', response.data);

            if (response.data.success) {
                const data = response.data.data;
                setCompatibilityData(data);
                setLoadingState('success');
                if (data.guid) {
                    // Naviguer vers l'URL canonique avec GUID
                    navigate(`/compatibilite-amoureuse-astrologie/${data.guid}/`, { replace: true });
                }
            } else {
                setError(response.data.error || 'Une erreur est survenue lors du calcul de compatibilité.');
                setLoadingState('error');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la soumission du formulaire:', error);
            setError(error.response?.data?.error || 'Une erreur est survenue lors de la communication avec le serveur.');
            setLoadingState('error');
        }
    };

    // Rendu du formulaire pour une personne
    const renderPersonForm = (personNumber) => {
        const personKey = `person${personNumber}`;
        const personData = formData[personKey];
        const citySearch = personNumber === 1 ? citySearch1 : citySearch2;
        const suggestions = personNumber === 1 ? citySuggestions1 : citySuggestions2;
        const showSuggestions = personNumber === 1 ? showSuggestions1 : showSuggestions2;

        return (
            <div className="person-form">
                <h3>
                    <span className="person-form-icon">
                        <i className={personNumber === 1 ? 'fa-solid fa-user' : 'fa-solid fa-user-plus'} aria-hidden="true"></i>
                    </span>
                    Personne {personNumber}
                </h3>
                <div className="form-group">
                    <label htmlFor={`${personKey}-first_name`}>Prénom</label>
                    <input
                        type="text"
                        id={`${personKey}-first_name`}
                        name="first_name"
                        value={personData.first_name}
                        onChange={(e) => handlePersonChange(e, personNumber)}
                        placeholder="Ex: Marie, Lucas..."
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${personKey}-birth_date`}>Date de naissance</label>
                    <input
                        type="date"
                        id={`${personKey}-birth_date`}
                        name="birth_date"
                        value={personData.birth_date}
                        onChange={(e) => handlePersonChange(e, personNumber)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${personKey}-birth_time`}>Heure de naissance</label>
                    <input
                        type="time"
                        id={`${personKey}-birth_time`}
                        name="birth_time"
                        value={personData.birth_time}
                        onChange={(e) => handlePersonChange(e, personNumber)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${personKey}-city`}>Lieu de naissance</label>
                    <div className="city-search-container">
                        <input
                            className="city-input"
                            type="text"
                            id={`${personKey}-city`}
                            value={citySearch}
                            onChange={(e) => handleCitySearch(e, personNumber)}
                            placeholder="Commencez à taper le nom d'une ville..."
                            required
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <ul className="city-suggestions">
                                {suggestions.map((suggestion, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleCitySelect(suggestion, personNumber)}
                                    >
                                        {suggestion.display}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Fonction pour afficher un score sous forme d'étoiles (score float 0-10)
    const renderScoreStars = (score) => {
        const maxScore = 10;
        const roundedScore = Math.round(score);
        const stars = [];

        for (let i = 1; i <= maxScore; i++) {
            if (i <= roundedScore) {
                stars.push(<span key={i} className="star filled">★</span>);
            } else {
                stars.push(<span key={i} className="star">☆</span>);
            }
        }

        return <div className="score-stars">{stars}</div>;
    };

    // Rendu des résultats de compatibilité (format backend v2 : score_global + sections)
    const renderCompatibilityResults = () => {
        if (!compatibilityData || !compatibilityData.compatibility) return null;

        const { compatibility, person1, person2 } = compatibilityData;
        const { score_global, synthese, sections } = compatibility;

        // Fonction utilitaire pour créer des liens vers les planètes dans l'affichage des résultats
        const getPlanetLink = (astreName, plural = false) => {
            const planetMap = {
                'Soleil': '/blog/planetes/soleil',
                'Lune': '/blog/planetes/lune',
                'Mercure': '/blog/planetes/mercure',
                'Vénus': '/blog/planetes/venus',
                'Mars': '/blog/planetes/mars',
                'Jupiter': '/blog/planetes/jupiter',
                'Saturne': '/blog/planetes/saturne',
                'Uranus': '/blog/planetes/uranus',
                'Neptune': '/blog/planetes/neptune',
                'Pluton': '/blog/planetes/pluton',
            };

            const baseName = astreName.replace(/s$/, '');
            const displayName = plural ? `${baseName}s` : astreName;

            if (planetMap[baseName]) {
                return <Link to={planetMap[baseName]}>{displayName}</Link>;
            }

            return displayName;
        };

        // Labels, icônes et descriptions pour chaque section
        const sectionMeta = {
            attirance: {
                title: 'Attraction Mutuelle',
                icon: 'fa-solid fa-fire',
                subtitle: <>{getPlanetLink('Vénus')}, {getPlanetLink('Mars')}, {getPlanetLink('Lune')} en synastrie</>
            },
            communication: {
                title: 'Communication',
                icon: 'fa-solid fa-comments',
                subtitle: <>{getPlanetLink('Mercure')} et éléments Air en synastrie</>
            },
            valeurs: {
                title: 'Valeurs Communes',
                icon: 'fa-solid fa-gem',
                subtitle: <>{getPlanetLink('Jupiter')}, {getPlanetLink('Saturne')}, {getPlanetLink('Vénus')} en synastrie</>
            },
            conflits: {
                title: 'Gestion des Conflits',
                icon: 'fa-solid fa-bolt',
                subtitle: <>{getPlanetLink('Mars')}, {getPlanetLink('Saturne')} et aspects durs en synastrie</>
            },
            long_terme: {
                title: 'Potentiel Long Terme',
                icon: 'fa-solid fa-infinity',
                subtitle: <>{getPlanetLink('Saturne')}, {getPlanetLink('Lune')} et aspects de stabilité</>
            }
        };

        // Verdict textuel dérivé du score global (même barème que le backend)
        const getVerdict = (score) => {
            if (score >= 86) return 'Compatibilité exceptionnelle';
            if (score >= 76) return 'Très bonne compatibilité';
            if (score >= 66) return 'Bonne compatibilité';
            if (score >= 51) return 'Compatibilité moyenne';
            if (score >= 31) return 'Compatibilité difficile';
            return 'Incompatibilité sérieuse';
        };

        // Anciennes synastries sans headline : on extrait l'accroche avant le " : " de la description
        const splitSection = (section) => {
            const desc = section.description || '';
            if (section.headline) return { headline: section.headline, description: desc };
            const idx = desc.indexOf(' : ');
            if (idx > 0 && idx <= 70) {
                const rest = desc.slice(idx + 3);
                return { headline: desc.slice(0, idx), description: rest.charAt(0).toUpperCase() + rest.slice(1) };
            }
            return { headline: null, description: desc };
        };

        const name1 = person1.first_name || 'Personne 1';
        const name2 = person2.first_name || 'Personne 2';

        return (
            <div className="compatibility-results panel">
                <h2>{name1} & {name2}</h2>

                <div className="score-hero">
                    <div className="score-hero-badge">
                        <span className="score-hero-value">{score_global}</span>
                        <span className="score-hero-max">/100</span>
                    </div>
                    <div className="score-hero-verdict">{getVerdict(score_global)}</div>
                    {renderScoreStars(Math.round(score_global / 10))}
                    {synthese && <p className="synthese">{synthese}</p>}
                </div>

                <div className="section-cards">
                    {sections && Object.entries(sectionMeta).map(([key, meta]) => {
                        const section = sections[key];
                        if (!section) return null;
                        const { headline, description } = splitSection(section);
                        return (
                            <div key={key} className="section-card">
                                <div className="section-card-top">
                                    <span className="section-card-icon">
                                        <i className={meta.icon} aria-hidden="true"></i>
                                    </span>
                                    <div className="section-card-titles">
                                        <h3>{meta.title}</h3>
                                        <div className="section-card-subtitle">{meta.subtitle}</div>
                                    </div>
                                    <div className="section-card-score">{section.score}<small>/10</small></div>
                                </div>
                                {headline && <p className="section-card-headline">{headline}</p>}
                                {renderScoreStars(section.score)}
                                <p className="section-card-desc">{description}</p>
                            </div>
                        );
                    })}
                </div>

                <div className="persons-info">
                    <div className="person-info">
                        <h3>{name1}</h3>
                        <p><strong>Date de naissance:</strong> {person1.identity.birth_date}</p>
                        <p><strong>Heure de naissance:</strong> {person1.identity.birth_time}</p>
                        <p><strong>Lieu de naissance:</strong> {person1.identity.birth_location.city}</p>
                    </div>
                    <div className="person-info">
                        <h3>{name2}</h3>
                        <p><strong>Date de naissance:</strong> {person2.identity.birth_date}</p>
                        <p><strong>Heure de naissance:</strong> {person2.identity.birth_time}</p>
                        <p><strong>Lieu de naissance:</strong> {person2.identity.birth_location.city}</p>
                    </div>
                </div>

                <div className="result-ctas">
                    <div className="result-cta sky">
                        <h3>Envie d'aller plus loin ?</h3>
                        <p>
                            Sur l'application Android, vos synastries restent sauvegardées et vous recevez
                            un horoscope personnalisé basé sur votre propre carte du ciel — pas seulement
                            votre signe.
                        </p>
                        <a
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="app-play-button"
                            aria-label="Télécharger Astralight sur Google Play"
                        >
                            <i className="fa-brands fa-google-play" aria-hidden="true"></i>
                            <span>
                                <small>Disponible sur</small>
                                Google Play
                            </span>
                        </a>
                    </div>
                    <div className="result-cta">
                        <h3>Explorez vos thèmes individuels</h3>
                        <p>
                            La synastrie compare vos deux ciels. Le thème astral individuel de {name1} ou
                            de {name2} révèle ce que chacun apporte à la relation : personnalité, besoins,
                            façon d'aimer.
                        </p>
                        <Link to="/calcul-theme-astral" className="btn-gold">
                            <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                            Calculer un thème astral
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    // Rendu du contenu principal
    const renderContent = () => {
        if (loadingState === 'loading') {
            return <Loader />;
        }

        if (compatibilityData) {
            return renderCompatibilityResults();
        }

        return null;
    };

    // Ajoutez également une gestion des clics en dehors si nécessaire
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Fermer les suggestions si on clique en dehors
            if (!event.target.closest('.city-search-container')) {
                setShowSuggestions1(false);
                setShowSuggestions2(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Synastrie Gratuite avec Interprétation | Compatibilité de Couple</title>
                <meta name="description" content="Calculez votre synastrie de couple gratuitement : tous les aspects entre vos deux thèmes astraux, 5 scores de compatibilité et une interprétation complète. Sans inscription." />
                
                {/* Balise canonique */}
                <link rel="canonical" href="https://astralight.xyz/compatibilite-amoureuse-astrologie" />
                
                {/* Balises Open Graph pour Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://astralight.xyz/compatibilite-amoureuse-astrologie" />
                <meta property="og:title" content="Synastrie Gratuite avec Interprétation | Compatibilité de Couple" />
                <meta property="og:description" content="Comparez vos deux thèmes astraux : aspects planétaires, scores de compatibilité et interprétation complète de votre relation, gratuitement." />
                <meta property="og:image" content="https://astralight.xyz/images/astralight_og.webp" />
                <meta property="og:site_name" content="AstraLight" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Synastrie Gratuite avec Interprétation | AstraLight" />
                <meta name="twitter:description" content="Testez la synastrie de votre couple : aspects Vénus-Mars, Soleil-Lune et tous les indicateurs clés, avec interprétation complète." />
                <meta name="twitter:image" content="https://astralight.xyz/images/astralight_og.webp" />
                
                {/* Autres balises méta importantes */}
                <meta name="robots" content="index, follow" />
                <meta name="keywords" content="synastrie gratuite, calcul synastrie, synastrie couple, synastrie gratuite avec interprétation, compatibilité astrologique, test compatibilité couple, aspects vénus mars, compatibilité amoureuse gratuite" />
                <meta name="author" content="AstraLight" />
                
                {/* Schemas structurés */}
                <script type="application/ld+json">
                    {JSON.stringify(getToolSchema())}
                </script>
                {/* <script type="application/ld+json">
                    {JSON.stringify(getPopularPairsSchema())}
                </script> */}
            </Helmet>
            
            <Navbar />
            
            <div className="container compatibility-container">
                {!guid && !compatibilityData && (
                    <PageHeader
                        eyebrow="Astrologie relationnelle"
                        title="Synastrie gratuite avec interprétation"
                        subtitle="Comparez vos deux thèmes astraux et découvrez la dynamique réelle de votre couple : attraction, communication, valeurs partagées, gestion des conflits et potentiel à long terme. Gratuit, immédiat et sans inscription."
                    >
                        <span className="page-header-chip">
                            <i className="fa-solid fa-heart" aria-hidden="true"></i>
                            Deux dates de naissance suffisent
                        </span>
                    </PageHeader>
                )}

                {!guid && !compatibilityData && (
                    <div className="compatibility-form-container panel">
                        <h2 className="form-title">Entrez les informations de naissance</h2>
                        <hr className="form-title-underline" />
                        <form onSubmit={handleSubmit}>
                            <div className="dual-form">
                                {renderPersonForm(1)}
                                {renderPersonForm(2)}
                            </div>
                            <button type="submit" className="submit-button btn-gold" disabled={loadingState === 'loading'}>
                                <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                                {loadingState === 'loading' ? 'Calcul en cours...' : 'Calculer notre synastrie'}
                            </button>
                        </form>
                        {error && <div className="error-message">{error}</div>}
                    </div>
                )}

                {/* Contenu informatif visible seulement s'il n'y a pas de résultats */}
                {!compatibilityData && <SynastrieInfoContent />}

                {/* Panneau de partage juste avant les résultats */}
                {compatibilityData?.guid && (
                    <ShareActions compatGuid={compatibilityData.guid} />
                )}

                {renderContent()}
                
                {/* Contenu informatif déplacé après les résultats */}
                {compatibilityData && <SynastrieInfoContent />}
                
                {/* Sections SEO toujours visibles */}
                <hr />
                <ScoreGuide />
                <FAQ />
            </div>

            <Footer />
        </>
    );
};

export default Compatibility; 